import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Sparkle } from "@phosphor-icons/react";
import { LotusBloom, SunBloom, MilestoneIcon } from "@/components/vansha/Graphics";

export default function Baby() {
  const [baby, setBaby] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", achieved_on: "", notes: "", category: "physical" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [b, m] = await Promise.all([
      api.get("/baby").catch(() => ({ data: null })),
      api.get("/milestones"),
    ]);
    setBaby(b.data);
    setMilestones(m.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/milestones", form);
      toast.success("Milestone saved.");
      setForm({ title: "", achieved_on: "", notes: "", category: "physical" });
      setAdding(false);
      load();
    } catch { toast.error("Could not save."); }
  };

  if (loading) return <div className="text-ink-500">Loading…</div>;
  if (!baby) return (
    <div data-testid="baby-empty">
      <h1 className="font-serif text-4xl mb-3">Baby</h1>
      <p className="text-ink-700 mb-6">No baby profile yet.</p>
      <Link to="/app/onboarding" className="btn-primary" data-testid="goto-onboarding">Add your baby</Link>
    </div>
  );

  const ageDays = Math.floor((Date.now() - new Date(baby.birth_date).getTime()) / 86400000);
  const ageMonths = Math.floor(ageDays / 30.44);

  return (
    <div className="space-y-8" data-testid="baby-page">
      <header className="vansha-card-tinted-sand p-8 sm:p-10 relative overflow-hidden">
        <div className="absolute right-0 -top-6 opacity-95 hidden md:block pointer-events-none">
          <LotusBloom size={200} />
        </div>
        <div className="absolute right-40 top-6 opacity-70 hidden lg:block pointer-events-none">
          <SunBloom size={150} />
        </div>
        <div className="relative z-10">
          <p className="overline text-terracotta mb-2">Digital twin</p>
          <h1 className="font-serif font-semibold text-5xl sm:text-6xl leading-none">
            {baby.name.split(" ")[0]}
            {baby.name.split(" ")[1] && <em className="text-terracotta font-normal italic"> {baby.name.split(" ").slice(1).join(" ")}</em>}
          </h1>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="baby-chips">
            <span className="chip chip-vermilion">{ageDays} days old</span>
            <span className="chip chip-marigold">{ageMonths} months</span>
            <span className="chip chip-sage">Born {new Date(baby.birth_date).toLocaleDateString()}</span>
            {baby.gender && <span className="chip capitalize">{baby.gender}</span>}
            {baby.blood_group && <span className="chip">Blood: {baby.blood_group}</span>}
            {baby.birth_weight_kg && <span className="chip">Birth weight: {baby.birth_weight_kg}kg</span>}
          </div>
        </div>
      </header>

      <section>
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <h2 className="font-serif text-3xl">Milestones</h2>
            <p className="text-ink-700 mt-1">Every first is a tiny miracle.</p>
          </div>
          <button onClick={() => setAdding(!adding)} className="btn-primary" data-testid="add-milestone-btn"><Plus weight="thin" size={18} /> Add milestone</button>
        </div>

        {adding && (
          <form onSubmit={submit} className="vansha-card p-6 space-y-5 mb-8" data-testid="milestone-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label className="overline mb-2 block">Milestone</label><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="input-line" placeholder="First smile" data-testid="ms-title" /></div>
              <div><label className="overline mb-2 block">Date</label><input type="date" required value={form.achieved_on} onChange={(e) => setForm({ ...form, achieved_on: e.target.value })} className="input-line" data-testid="ms-date" /></div>
            </div>
            <div><label className="overline mb-2 block">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-line bg-transparent" data-testid="ms-category">
                <option value="physical">Physical</option><option value="motor">Motor</option><option value="speech">Speech</option><option value="social">Social</option><option value="first">First</option>
              </select>
            </div>
            <div><label className="overline mb-2 block">Notes (optional)</label><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-line" rows="2" data-testid="ms-notes" /></div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setAdding(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="ms-submit">Save</button>
            </div>
          </form>
        )}

        {milestones.length === 0 ? (
          <p className="text-ink-500" data-testid="milestone-empty">No milestones logged yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {milestones.map((m) => (
              <div key={m.id} className="vansha-card p-6" data-testid={`milestone-${m.id}`}>
                <Sparkle weight="thin" size={22} className="text-saffron mb-2" />
                <p className="overline mb-1">{new Date(m.achieved_on).toLocaleDateString()}</p>
                <h3 className="font-serif text-xl">{m.title}</h3>
                {m.notes && <p className="text-ink-700 text-sm mt-2">{m.notes}</p>}
                <p className="overline mt-3 text-saffron capitalize">{m.category}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
